<template>
  <div class="w-full">
    <!-- HERO SECTION -->
    <div class="relative w-full lg:h-[70vh] h-[30vh]">
      <div class="absolute inset-0 overflow-hidden">
        <img
          src="../assets/images/HERO Pages ALL 2.png"
          alt="Background"
          class="w-full h-full object-cover"
        />
        <div class="absolute inset-0 bg-black opacity-50"></div>
      </div>

      <div
        class="relative max-w-screen-xl mx-auto h-full flex flex-col items-center justify-center px-6 text-center"
      >
        <h2
          class="text-white text-center text-3xl font-black animate__animated animate__backInLeft"
        >
          Achievements
        </h2>
      </div>

      <!-- SVG Curve -->
      <svg viewBox="0 0 1440 320" class="absolute bottom-0 left-0 w-full">
        <path
          fill="#ffffff"
          d="M0,224L80,208C160,192,320,160,480,176C640,192,800,256,960,266.7C1120,277,1280,235,1360,213.3L1440,192V320H0Z"
        ></path>
      </svg>
    </div>

    <section
      class="py-16 px-6 md:px-10 max-w-screen-xl mx-auto text-gray-900 relative z-10 lg:-mt-32"
    >
      <div class="strategic-card">
        <h3 class="text-3xl font-bold text-start mb-4">Our Milestones</h3>
        <p class="text-start text-base text-gray-600 mb-12">
          Celebrating our journey of excellence, innovation, and impact.
        </p>
      </div>

      <div class="space-y-12 relative border-l-4 border-blue pl-6">
        <div
          v-for="(milestone, index) in milestones"
          :key="index"
          class="relative"
        >
          <div
            class="absolute left-[-13px] top-2 w-6 h-6 bg-blue-600 rounded-full border-4 border-yellow shadow-md"
          ></div>
          <div
            class="bg-white p-6 rounded-lg shadow hover:shadow-lg transition"
          >
            <h4 class="text-xl font-semibold">{{ milestone.title }}</h4>
            <p class="text-sm text-gray-500">{{ milestone.date }}</p>
            <p class="mt-2 text-gray-700">{{ milestone.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- DOCUMENT DOWNLOAD SECTION -->
    <section class="py-16 px-6 md:px-10 max-w-screen-xl mx-auto">
      <h3 class="text-3xl font-bold text-center mb-12">
        Achievement Documents
      </h3>
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
        <div
          v-for="(doc, index) in documents"
          :key="index"
          class="bg-white rounded-lg shadow hover:shadow-xl transition overflow-hidden cursor-pointer"
          @click="openImage(doc.image)"
        >
          <img
            :src="doc.image"
            :alt="doc.title"
            class="w-full h-64 object-cover"
          />
          <div class="p-4">
            <h4 class="font-semibold text-lg">{{ doc.title }}</h4>
            <p class="text-sm text-gray-500 mt-1">{{ doc.description }}</p>
          </div>
        </div>

        <!-- Modal -->
        <div
          v-if="selectedImage"
          class="fixed inset-0 bg-black bg-opacity-90 z-50 flex justify-center items-center p-4 overflow-y-auto"
          @click.self="closeImage"
        >
          <div class="relative max-w-5xl w-full max-h-full">
            <img
              :src="selectedImage"
              alt="Full Image"
              class="w-full h-auto max-h-[90vh] object-contain rounded-lg shadow-lg"
            />
            <button
              @click="closeImage"
              class="absolute top-2 right-2 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 text-xl"
            >
              &times;
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- IMPACT STATS -->
    <section class="bg-blue py-16">
      <div
        class="max-w-screen-xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center px-6"
      >
        <div>
          <h4 class="text-4xl font-bold text-white">20+</h4>
          <p class="text-sm text-[#ada696] mt-2">Projects Delivered</p>
        </div>
        <div>
          <h4 class="text-4xl font-bold text-white">30</h4>
          <p class="text-sm text-[#ada696] mt-2">Communities Impacted</p>
        </div>
        <div>
          <h4 class="text-4xl font-bold text-white">10+</h4>
          <p class="text-sm text-[#ada696] mt-2">Awards Received</p>
        </div>
        <div>
          <h4 class="text-4xl font-bold text-white">100%</h4>
          <p class="text-sm text-[#ada696] mt-2">Client Satisfaction</p>
        </div>
      </div>
    </section>

    <!-- QUOTE SECTION -->
    <section class="py-12 px-6 max-w-screen-md mx-auto text-center">
      <blockquote
        class="italic text-lg text-gray-800 border-l-4 border-blue pl-4"
      >
        "Our achievements reflect the dedication of our team and the trust of
        our community."
      </blockquote>
    </section>

    <!-- CALL TO ACTION -->
    <section class="text-center py-12">
      <h4 class="text-xl font-bold mb-4">Want to learn more?</h4>
      <router-link
        to="/contact-us"
        class="inline-block bg-yellow text-white px-6 py-2 hover:bg-black transition"
      >
        Contact Us
      </router-link>
    </section>
  </div>
</template>

<script>
export default {
  data() {
    return {
      selectedImage: null,
      milestones: [
        {
          title: "Company Founded",
          date: "2018",
          description: "Started with a vision to redefine excellence.",
        },
        {
          title: "First Major Project",
          date: "2019",
          description: "Delivered our first nationwide campaign.",
        },
        {
          title: "Local Expansion",
          date: "2024",
          description: "Opened offices in 3 new states in Nigeria.",
        },
        {
          title: "Global Expansion",
          date: "2025",
          description: "Opened offices in 2 new countries.",
        },
        {
          title: "Awarded Excellence",
          date: "2021",
          description: "Received national recognition for innovation.",
        },
      ],
      documents: [
        {
          title: "CAC Document",
          description: "Highlights of our journey through the year.",
          image: require("../assets/documents/thumbnails/cac-document.png"),
        },
        {
          title: "HMO Document",
          description: "How we gave back to our community.",
          image: require("../assets/documents/thumbnails/hmo-document.png"),
        },
        {
          title: "ITF Document",
          description: "Our commitment to a greener future.",
          image: require("../assets/documents/thumbnails/itf-document.png"),
        },
        {
          title: "NSITF Document",
          description: "Milestones of our international growth.",
          image: require("../assets/documents/thumbnails/nsitf-document.png"),
        },
        {
          title: "REDAN Document",
          description: "Breakthroughs and success stories.",
          image: require("../assets/documents/thumbnails/redan-document.png"),
        },
      ],
    };
  },
  methods: {
    openImage(imageUrl) {
      this.selectedImage = imageUrl;
    },
    closeImage() {
      this.selectedImage = null;
    },
  },
  mounted() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in");
          } else {
            entry.target.classList.remove("fade-in");
          }
        });
      },
      {
        threshold: 0.2, // Adjust for smoother animation trigger
      }
    );

    document
      .querySelectorAll(".strategic-card")
      .forEach((card) => observer.observe(card));
  },
};
</script>

<style scoped>
svg path {
  fill: #ffffff !important;
  stroke: none !important;
}

svg {
  overflow: hidden !important;
}

.absolute.bottom-0 {
  line-height: 0 !important;
  margin-bottom: -2px;
}

.strategic-card {
  opacity: 0;
  transform: translateY(50px);
  transition: opacity 1s ease-out, transform 1s ease-out;
  will-change: opacity, transform;
}

.strategic-card.fade-in {
  opacity: 1;
  transform: translateY(0);
}

.strategic-card:hover {
  transform: scale(1.03);
  /* box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2); */
  /* transition: transform 0.4s ease-out, box-shadow 0.4s ease-out; */
  /* background-color: inherit !important; */
  box-shadow: none !important;
}
</style>
